// src/pages/delivery/DeliveryBoysPage.jsx
import React, { useState, useEffect } from "react";
import {
    Container,
    Card,
    Table,
    Button,
    Form,
    Row,
    Col,
    InputGroup,
    Alert,
    Spinner,
    Modal,
    Badge,
    Tabs,
    Tab,
} from "react-bootstrap";
import {
    FaPlus,
    FaEdit,
    FaTrash,
    FaEye,
    FaSearch,
    FaFilter,
    FaTimes,
    FaUser,
    FaPhone,
    FaStar,
    FaCheckCircle,
    FaClock,
    FaBan,
    FaInfoCircle,
    FaTruck,
    FaMoneyBillWave,
    FaArrowLeft,
    FaUsers,
    FaCar,
    FaRoute,
    FaCalendarAlt,
} from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { ToastContainer, toast, Bounce } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Swal from "sweetalert2";
import { deliveryBoyApi } from "../../api/tenant/delivery.api";
import "../../styles/styles.css";

const DeliveryBoysPage = () => {
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState("boys");
    const [deliveryBoys, setDeliveryBoys] = useState([]);
    const [searchTerm, setSearchTerm] = useState("");
    const [statusFilter, setStatusFilter] = useState("all");
    const [currentStatusFilter, setCurrentStatusFilter] = useState("all");
    const [loading, setLoading] = useState(true);
    const [errorMessage, setErrorMessage] = useState("");
    const [showViewModal, setShowViewModal] = useState(false);
    const [selectedDeliveryBoy, setSelectedDeliveryBoy] = useState(null);
    const [stats, setStats] = useState(null);

    const STATUS_CONFIG = {
        available: { bg: "#ECFDF3", color: "#027A48", label: "Available", icon: <FaCheckCircle /> },
        on_delivery: { bg: "#D3EAFF", color: "#437EF7", label: "On Delivery", icon: <FaTruck /> },
        off_duty: { bg: "#FEF6D7", color: "#FED229", label: "Off Duty", icon: <FaClock /> },
        on_leave: { bg: "#FFE4E6", color: "#E11D48", label: "On Leave", icon: <FaBan /> },
    };

    useEffect(() => {
        loadDeliveryBoys();
        loadStats();
    }, []);

    const loadDeliveryBoys = async () => {
        setLoading(true);
        setErrorMessage("");
        try {
            const response = await deliveryBoyApi.getAll();
            const data = response?.data?.data?.data || response?.data?.data || response?.data || [];
            setDeliveryBoys(Array.isArray(data) ? data : []);
        } catch (error) {
            console.error("Failed to load delivery boys:", error);
            setErrorMessage(error.response?.data?.message || "Failed to load delivery boys");
            toast.error("Failed to load delivery boys");
            setDeliveryBoys([]);
        } finally {
            setLoading(false);
        }
    };

    const loadStats = async () => {
        try {
            const response = await deliveryBoyApi.getStats();
            const data = response?.data?.data || response?.data || {};
            setStats(data);
        } catch (error) {
            console.error("Failed to load stats:", error);
        }
    };

    const handleView = (deliveryBoy) => {
        setSelectedDeliveryBoy(deliveryBoy);
        setShowViewModal(true);
    };

    const handleEdit = (id) => navigate(`/delivery/boys/edit/${id}`);
    const handleAdd = () => navigate("/delivery/boys/add");

    const handleDelete = async (id, name) => {
        const result = await Swal.fire({
            title: "Are you sure?",
            text: `Delete delivery boy "${name}"?`,
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#d33",
            cancelButtonColor: "#3085d6",
            confirmButtonText: "Yes, delete it!",
        });

        if (result.isConfirmed) {
            try {
                await deliveryBoyApi.delete(id);
                toast.success(`✅ Delivery boy "${name}" deleted successfully!`);
                await loadDeliveryBoys();
                await loadStats();
            } catch (error) {
                toast.error(error.response?.data?.message || "Failed to delete delivery boy");
            }
        }
    };

    const filteredDeliveryBoys = deliveryBoys.filter((boy) => {
        const matchesSearch =
            searchTerm === "" ||
            boy.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            boy.mobile?.includes(searchTerm) ||
            boy.employee_code?.toLowerCase().includes(searchTerm.toLowerCase());
        const matchesStatus = statusFilter === "all" || boy.status === statusFilter;
        const matchesCurrentStatus =
            currentStatusFilter === "all" || boy.current_status === currentStatusFilter;
        return matchesSearch && matchesStatus && matchesCurrentStatus;
    });

    const clearFilters = () => {
        setSearchTerm("");
        setStatusFilter("all");
        setCurrentStatusFilter("all");
    };

    const statsData = stats || {
        total: deliveryBoys.length,
        available: deliveryBoys.filter((b) => b.current_status === "available").length,
        on_delivery: deliveryBoys.filter((b) => b.current_status === "on_delivery").length,
        off_duty: deliveryBoys.filter((b) => b.current_status === "off_duty").length,
        on_leave: deliveryBoys.filter((b) => b.current_status === "on_leave").length,
    };

    const formatDate = (dateString) => {
        if (!dateString) return "N/A";
        return new Date(dateString).toLocaleDateString("en-IN", {
            year: "numeric",
            month: "short",
            day: "numeric",
        });
    };

    const getStatusBadge = (status) => {
        const config = STATUS_CONFIG[status?.toLowerCase()] || {
            bg: "#F1F3F5",
            color: "#6B7280",
            label: status || "Unknown",
            icon: <FaInfoCircle />,
        };
        return (
            <span
                style={{
                    padding: "6px 14px",
                    borderRadius: "20px",
                    fontWeight: "600",
                    fontSize: "13px",
                    backgroundColor: config.bg,
                    color: config.color,
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "6px",
                }}
            >
                {config.icon} {config.label}
            </span>
        );
    };

    if (loading) {
        return (
            <Container fluid className="p-4" style={{ backgroundColor: "#f8f9fa", minHeight: "100vh" }}>
                <ToastContainer position="top-right" autoClose={3000} transition={Bounce} />
                <div className="text-center py-5">
                    <Spinner animation="border" variant="secondary" size="lg" />
                    <h4 className="mt-3">Loading delivery management...</h4>
                </div>
            </Container>
        );
    }

    return (
        <Container fluid className="px-4 py-3" style={{ backgroundColor: "#f8f9fa", minHeight: "100vh" }}>
            <ToastContainer position="top-right" autoClose={3000} transition={Bounce} />

            <div className="d-flex justify-content-between align-items-center mb-4">
                <div>
                    <h2 className="fw-bold mb-1" style={{ color: "rgb(30, 58, 111)" }}>
                        <FaTruck className="me-2" /> Delivery Management
                    </h2>
                    <p className="text-muted">Manage delivery personnel, vehicles, routes and schedules</p>
                </div>
            </div>

            {/* Stats Cards */}
            <Row className="g-3 mb-4">
                <Col md={2.4}>
                    <Card className="border-0 shadow-sm rounded-3">
                        <Card.Body className="py-2">
                            <small className="text-muted">Total</small>
                            <h3 className="fw-bold mb-0">{statsData.total}</h3>
                        </Card.Body>
                    </Card>
                </Col>
                <Col md={2.4}>
                    <Card className="border-0 shadow-sm rounded-3" style={{ borderLeft: "4px solid #027A48" }}>
                        <Card.Body className="py-2">
                            <small className="text-muted">Available</small>
                            <h3 className="fw-bold mb-0" style={{ color: "#027A48" }}>{statsData.available}</h3>
                        </Card.Body>
                    </Card>
                </Col>
                <Col md={2.4}>
                    <Card className="border-0 shadow-sm rounded-3" style={{ borderLeft: "4px solid #437EF7" }}>
                        <Card.Body className="py-2">
                            <small className="text-muted">On Delivery</small>
                            <h3 className="fw-bold mb-0" style={{ color: "#437EF7" }}>{statsData.on_delivery}</h3>
                        </Card.Body>
                    </Card>
                </Col>
                <Col md={2.4}>
                    <Card className="border-0 shadow-sm rounded-3" style={{ borderLeft: "4px solid #FED229" }}>
                        <Card.Body className="py-2">
                            <small className="text-muted">Off Duty</small>
                            <h3 className="fw-bold mb-0" style={{ color: "#FED229" }}>{statsData.off_duty}</h3>
                        </Card.Body>
                    </Card>
                </Col>
                <Col md={2.4}>
                    <Card className="border-0 shadow-sm rounded-3" style={{ borderLeft: "4px solid #E11D48" }}>
                        <Card.Body className="py-2">
                            <small className="text-muted">On Leave</small>
                            <h3 className="fw-bold mb-0" style={{ color: "#E11D48" }}>{statsData.on_leave}</h3>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            <Card className="border-0 shadow-sm rounded-3">
                <Card.Body className="p-0">
                    <Tabs
                        activeKey={activeTab}
                        onSelect={(k) => setActiveTab(k)}
                        className="border-bottom px-3 pt-2"
                        style={{ backgroundColor: "#f8f9fa", borderTopLeftRadius: "0.75rem", borderTopRightRadius: "0.75rem" }}
                    >
                        <Tab eventKey="boys" title={<span><FaUsers className="me-2" />Delivery Boys</span>}>
                            <div className="p-3">
                                {/* Header */}
                                <div className="d-flex justify-content-between align-items-center mb-3">
                                    <div>
                                        <h5 className="fw-bold mb-0">Delivery Boys</h5>
                                        <small className="text-muted">Manage all delivery personnel</small>
                                    </div>
                                    <Button
                                        onClick={handleAdd}
                                        variant="primary"
                                        size="sm"
                                        style={{
                                            backgroundColor: "rgb(30, 58, 111)",
                                            border: "none",
                                            borderRadius: "8px",
                                            padding: "8px 16px",
                                        }}
                                    >
                                        <FaPlus className="me-1" /> Add Delivery Boy
                                    </Button>
                                </div>

                                {/* Filters */}
                                <Row className="mb-3">
                                    <Col md={4}>
                                        <InputGroup size="sm">
                                            <InputGroup.Text style={{ backgroundColor: "#fff", borderRight: "none" }}>
                                                <FaSearch className="text-muted" />
                                            </InputGroup.Text>
                                            <Form.Control
                                                type="text"
                                                placeholder="Search by name, mobile or code..."
                                                value={searchTerm}
                                                onChange={(e) => setSearchTerm(e.target.value)}
                                                style={{ borderLeft: "none" }}
                                            />
                                            {searchTerm && (
                                                <Button variant="outline-secondary" onClick={() => setSearchTerm("")}>
                                                    <FaTimes />
                                                </Button>
                                            )}
                                        </InputGroup>
                                    </Col>
                                    <Col md={3}>
                                        <Form.Select size="sm" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                                            <option value="all">All Status</option>
                                            <option value="active">Active</option>
                                            <option value="inactive">Inactive</option>
                                            <option value="blocked">Blocked</option>
                                        </Form.Select>
                                    </Col>
                                    <Col md={3}>
                                        <Form.Select size="sm" value={currentStatusFilter} onChange={(e) => setCurrentStatusFilter(e.target.value)}>
                                            <option value="all">All Current Status</option>
                                            <option value="available">Available</option>
                                            <option value="on_delivery">On Delivery</option>
                                            <option value="off_duty">Off Duty</option>
                                            <option value="on_leave">On Leave</option>
                                        </Form.Select>
                                    </Col>
                                    <Col md={2}>
                                        <Button variant="outline-secondary" size="sm" onClick={clearFilters} className="w-100">
                                            <FaFilter /> Clear
                                        </Button>
                                    </Col>
                                </Row>

                                {/* Results count */}
                                <div className="mb-2">
                                    <small className="text-muted">
                                        Showing {filteredDeliveryBoys.length} of {deliveryBoys.length} delivery boys
                                    </small>
                                </div>

                                {/* Table */}
                                <div className="table-responsive">
                                    <Table hover size="sm" style={{ fontSize: "13px" }}>
                                        <thead style={{ backgroundColor: "#f8f9fa" }}>
                                            <tr>
                                                <th>Code</th>
                                                <th>Name</th>
                                                <th>Mobile</th>
                                                <th>Status</th>
                                                <th>Current Status</th>
                                                <th>Rating</th>
                                                <th>Deliveries</th>
                                                <th style={{ width: "60px" }}>Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {filteredDeliveryBoys.length > 0 ? (
                                                filteredDeliveryBoys.map((boy) => (
                                                    <tr key={boy.id}>
                                                        <td><span className="fw-semibold" style={{ color: "rgb(30, 58, 111)" }}>{boy.employee_code || "N/A"}</span></td>
                                                        <td>{boy.name}</td>
                                                        <td>{boy.mobile}</td>
                                                        <td><Badge bg={boy.status === "active" ? "success" : "danger"}>{boy.status}</Badge></td>
                                                        <td>{getStatusBadge(boy.current_status)}</td>
                                                        <td>{boy.rating ? <><FaStar style={{ color: "#FED229" }} /> {boy.rating}</> : "N/A"}</td>
                                                        <td>{boy.total_deliveries || 0}</td>
                                                        <td>
                                                            <div className="d-flex gap-1">
                                                                <Button variant="light" size="sm" onClick={() => handleView(boy)} className="p-1 px-2">
                                                                    <FaEye size={12} />
                                                                </Button>
                                                                <Button variant="light" size="sm" onClick={() => handleEdit(boy.id)} className="p-1 px-2">
                                                                    <FaEdit size={12} style={{ color: "#ff9800" }} />
                                                                </Button>
                                                                <Button variant="light" size="sm" onClick={() => handleDelete(boy.id, boy.name)} className="p-1 px-2">
                                                                    <FaTrash size={12} style={{ color: "#dc3545" }} />
                                                                </Button>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                ))
                                            ) : (
                                                <tr>
                                                    <td colSpan="8" className="text-center py-4">
                                                        <FaUser size={30} className="text-muted mb-2" />
                                                        <p className="text-muted mb-0">No delivery boys found</p>
                                                    </td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </Table>
                                </div>
                            </div>
                        </Tab>

                        <Tab eventKey="vehicles" title={<span><FaCar className="me-2" />Vehicles</span>}>
                            <div className="p-4 text-center text-muted">
                                <FaCar size={40} className="mb-3" />
                                <h5>Vehicles Management</h5>
                                <p>Coming soon...</p>
                            </div>
                        </Tab>

                        <Tab eventKey="routes" title={<span><FaRoute className="me-2" />Routes</span>}>
                            <div className="p-4 text-center text-muted">
                                <FaRoute size={40} className="mb-3" />
                                <h5>Routes Management</h5>
                                <p>Coming soon...</p>
                            </div>
                        </Tab>

                        <Tab eventKey="schedules" title={<span><FaCalendarAlt className="me-2" />Schedules</span>}>
                            <div className="p-4 text-center text-muted">
                                <FaCalendarAlt size={40} className="mb-3" />
                                <h5>Schedules Management</h5>
                                <p>Coming soon...</p>
                            </div>
                        </Tab>
                    </Tabs>
                </Card.Body>
            </Card>

            {/* View Modal */}
            <Modal show={showViewModal} onHide={() => setShowViewModal(false)} size="lg" centered>
                <Modal.Header closeButton className="bg-dark text-white rounded-top-3 border-0">
                    <Modal.Title><FaEye className="me-2" /> Delivery Boy Details</Modal.Title>
                </Modal.Header>
                <Modal.Body className="p-4">
                    {selectedDeliveryBoy && (
                        <Row>
                            <Col md={6}>
                                <InfoCard title="Personal Information" icon={<FaUser />}>
                                    <InfoRow label="Employee Code" value={selectedDeliveryBoy.employee_code || "N/A"} highlight />
                                    <InfoRow label="Name" value={selectedDeliveryBoy.name} highlight />
                                    <InfoRow label="Mobile" value={selectedDeliveryBoy.mobile} />
                                    <InfoRow label="Alternate Mobile" value={selectedDeliveryBoy.alternate_mobile || "N/A"} />
                                    <InfoRow label="Email" value={selectedDeliveryBoy.email || "N/A"} />
                                    <InfoRow label="Address" value={selectedDeliveryBoy.address || "N/A"} />
                                    <InfoRow label="Join Date" value={formatDate(selectedDeliveryBoy.join_date)} />
                                </InfoCard>
                            </Col>
                            <Col md={6}>
                                <InfoCard title="Status Information" icon={<FaInfoCircle />}>
                                    <InfoRow label="Status" value={<Badge bg={selectedDeliveryBoy.status === "active" ? "success" : "danger"}>{selectedDeliveryBoy.status}</Badge>} isHtml />
                                    <InfoRow label="Current Status" value={getStatusBadge(selectedDeliveryBoy.current_status)} isHtml />
                                    <InfoRow label="Rating" value={selectedDeliveryBoy.rating || "N/A"} />
                                    <InfoRow label="Total Deliveries" value={selectedDeliveryBoy.total_deliveries || 0} />
                                    <InfoRow label="Total Earnings" value={`₹${selectedDeliveryBoy.total_earnings || 0}`} />
                                </InfoCard>
                                <InfoCard title="Salary Information" icon={<FaMoneyBillWave />} className="mt-3">
                                    <InfoRow label="Salary Type" value={selectedDeliveryBoy.salary_type || "N/A"} />
                                    <InfoRow label="Base Salary" value={`₹${selectedDeliveryBoy.base_salary || 0}`} />
                                    <InfoRow label="Commission Rate" value={`${selectedDeliveryBoy.commission_rate || 0}%`} />
                                    <InfoRow label="Commission Per Delivery" value={`₹${selectedDeliveryBoy.commission_per_delivery || 0}`} />
                                </InfoCard>
                            </Col>
                        </Row>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="outline-secondary" onClick={() => setShowViewModal(false)}>Close</Button>
                    <Button variant="primary" onClick={() => { setShowViewModal(false); handleEdit(selectedDeliveryBoy?.id); }}>
                        <FaEdit className="me-2" /> Edit
                    </Button>
                </Modal.Footer>
            </Modal>
        </Container>
    );
};

const InfoCard = ({ title, icon, children, className }) => (
    <Card className={`border-0 shadow-sm rounded-3 h-100 ${className || ''}`}>
        <Card.Header className="bg-white border-bottom pt-3 pb-2">
            <h6 className="fw-bold mb-0" style={{ color: "rgb(30, 58, 111)" }}>{icon} {title}</h6>
        </Card.Header>
        <Card.Body>{children}</Card.Body>
    </Card>
);

const InfoRow = ({ label, value, isHtml, highlight }) => (
    <div className="mb-2 pb-2 border-bottom">
        <small className="text-muted">{label}</small>
        <div className={highlight ? "fw-bold" : ""} style={highlight ? { color: "rgb(30, 58, 111)" } : {}}>
            {isHtml ? value : <span>{value}</span>}
        </div>
    </div>
);

export default DeliveryBoysPage;